import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { MapPin, Calendar, Clock, Download, ExternalLink, ArrowLeft, Music, Heart, Ticket, Share2, Copy, Check } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "../components/ui/dropdown-menu";

// Fix leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventDetail() {
  const { eventId } = useParams();
  const { user, token } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [copied, setCopied] = useState(false);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  // Share functions
  const getShareUrl = () => {
    return window.location.href;
  };

  const getShareText = () => {
    if (!event) return "";
    const date = format(new Date(event.event_date), "d MMM yyyy", { locale: es });
    return `${event.title} - ${date} en ${event.bar?.name || "Rockmaps"}`;
  };

  const shareToWhatsApp = () => {
    const text = encodeURIComponent(`${getShareText()}\n${getShareUrl()}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const shareToFacebook = () => {
    const url = encodeURIComponent(getShareUrl());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank");
  };

  const shareToTwitter = () => {
    const text = encodeURIComponent(getShareText());
    const url = encodeURIComponent(getShareUrl());
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, "_blank");
  };

  const shareToInstagram = () => {
    // Instagram doesn't have a direct share URL, copy to clipboard instead
    copyLink();
    toast.info("Link copiado. Pégalo en tu historia de Instagram");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      toast.success("LINK COPIADO");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("ERROR AL COPIAR");
    }
  };

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await axios.get(`${API}/events/${eventId}`);
        setEvent(res.data);
        
        // Track event view for analytics
        if (res.data.bar_id) {
          axios.post(`${API}/bars/${res.data.bar_id}/track?analytics_type=event_view&event_id=${eventId}`).catch(() => {});
        }
        
        // Check if favorited
        if (token) {
          try {
            const favRes = await axios.get(`${API}/favorites/${eventId}/check`, { headers: authHeaders });
            setIsFavorite(favRes.data.is_favorite);
          } catch (e) {
            // Ignore favorites check errors
          }
        }
      } catch (error) {
        console.error("Error fetching event:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId, token]);

  const toggleFavorite = async () => {
    if (!token) {
      toast.error("INICIA SESIÓN PARA GUARDAR FAVORITOS");
      return;
    }
    
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${eventId}`, { headers: authHeaders });
        setIsFavorite(false);
        toast.success("ELIMINADO DE FAVORITOS");
      } else {
        await axios.post(`${API}/favorites/${eventId}`, {}, { headers: authHeaders });
        setIsFavorite(true);
        toast.success("AÑADIDO A FAVORITOS");
      }
    } catch (error) {
      toast.error("ERROR AL ACTUALIZAR FAVORITOS");
    }
  };

  const downloadICS = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/ics`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${eventId}.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("ARCHIVO .ICS DESCARGADO");
    } catch (error) {
      toast.error("ERROR AL DESCARGAR");
    }
  };

  const addToGoogleCalendar = () => {
    if (!event) return;
    
    const startDate = new Date(event.event_date);
    const endDate = event.end_date ? new Date(event.end_date) : new Date(startDate.getTime() + 3 * 60 * 60 * 1000);
    
    const formatGoogleDate = (date) => {
      return date.toISOString().replace(/-|:|\.\d\d\d/g, "");
    };
    
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: event.title,
      dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
      details: event.description || "",
      location: event.bar?.address || "",
    });
    
    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="h-64 bg-[#111] mb-8" />
            <div className="h-10 bg-[#111] w-2/3 mb-4" />
            <div className="h-4 bg-[#111] w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <h1 className="text-3xl font-black text-white mb-4">EVENTO NO ENCONTRADO</h1>
          <Link to="/events" className="text-primary font-mono">
            VOLVER A EVENTOS
          </Link>
        </div>
      </div>
    );
  }

  const bar = event.bar;
  const hasLocation = bar?.latitude && bar?.longitude;

  const formatEventDate = (date) => {
    try {
      return format(new Date(date), "EEEE d 'de' MMMM 'de' yyyy", { locale: es });
    } catch {
      return "";
    }
  };

  const formatEventTime = (date) => {
    try {
      return format(new Date(date), "HH:mm");
    } catch {
      return "";
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      {/* Back Link */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Link 
          to="/events" 
          className="inline-flex items-center gap-2 font-mono text-sm text-zinc-500 hover:text-primary transition-colors"
          data-testid="back-to-events"
        >
          <ArrowLeft className="w-4 h-4" />
          VOLVER A EVENTOS
        </Link>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Event Image */}
        <div className="relative aspect-video mb-8 overflow-hidden border border-zinc-800">
          {event.image_url ? (
            <img
              src={event.image_url}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-zinc-900 to-zinc-800 flex items-center justify-center">
              <Music className="w-24 h-24 text-zinc-700" />
            </div>
          )}
          {event.price && (
            <div className="absolute top-4 right-4 bg-primary text-white font-mono px-4 py-2 text-lg">
              {event.price}
            </div>
          )}
          {/* Favorite Button */}
          <button
            onClick={toggleFavorite}
            className={`absolute top-4 left-4 p-3 transition-all ${
              isFavorite 
                ? "bg-primary text-white" 
                : "bg-black/50 text-white hover:bg-primary"
            }`}
            data-testid="toggle-favorite"
          >
            <Heart className={`w-6 h-6 ${isFavorite ? "fill-current" : ""}`} />
          </button>
        </div>

        {/* Event Info */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tighter mb-6" data-testid="event-title">
            {event.title}
          </h1>

          {/* Date & Time */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-3 bg-[#111] border border-zinc-800 px-4 py-3">
              <Calendar className="w-5 h-5 text-primary" />
              <span className="font-mono text-sm text-white capitalize" data-testid="event-date">
                {formatEventDate(event.event_date)}
              </span>
            </div>
            <div className="flex items-center gap-3 bg-[#111] border border-zinc-800 px-4 py-3">
              <Clock className="w-5 h-5 text-primary" />
              <span className="font-mono text-sm text-white" data-testid="event-time">
                {formatEventTime(event.event_date)}H
                {event.end_date && ` - ${formatEventTime(event.end_date)}H`}
              </span>
            </div>
            
            {/* Share Button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-3 bg-[#111] border border-zinc-800 px-4 py-3 hover:border-primary transition-colors"
                  data-testid="share-button"
                >
                  <Share2 className="w-5 h-5 text-primary" />
                  <span className="font-mono text-sm text-white">COMPARTIR</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#111] border-zinc-800 rounded-none min-w-[180px]">
                <DropdownMenuItem 
                  onClick={shareToWhatsApp}
                  className="font-mono text-sm cursor-pointer hover:bg-zinc-800 rounded-none"
                >
                  <svg className="w-4 h-4 mr-3 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={shareToFacebook}
                  className="font-mono text-sm cursor-pointer hover:bg-zinc-800 rounded-none"
                >
                  <svg className="w-4 h-4 mr-3 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={shareToTwitter}
                  className="font-mono text-sm cursor-pointer hover:bg-zinc-800 rounded-none"
                >
                  <svg className="w-4 h-4 mr-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  X (Twitter)
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={shareToInstagram}
                  className="font-mono text-sm cursor-pointer hover:bg-zinc-800 rounded-none"
                >
                  <svg className="w-4 h-4 mr-3 text-pink-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  Instagram
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={copyLink}
                  className="font-mono text-sm cursor-pointer hover:bg-zinc-800 rounded-none"
                >
                  {copied ? (
                    <Check className="w-4 h-4 mr-3 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4 mr-3 text-zinc-400" />
                  )}
                  {copied ? "¡Copiado!" : "Copiar link"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Add to Calendar Buttons */}
          <div className="flex flex-wrap gap-3 mb-8">
            {/* Buy Tickets Button - Show first if available */}
            {event.ticket_url && (
              <a
                href={event.ticket_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  // Track ticket click for analytics
                  if (event.bar_id) {
                    axios.post(`${API}/bars/${event.bar_id}/track?analytics_type=ticket_click&event_id=${eventId}`).catch(() => {});
                  }
                }}
                className="btn-ruido bg-green-600 text-white font-mono border-2 border-green-600 px-6 py-2 flex items-center gap-2 hover:bg-green-700"
                data-testid="buy-tickets"
              >
                <Ticket className="w-4 h-4" />
                COMPRAR ENTRADAS
              </a>
            )}
            {/* Ticket Info Text */}
            {event.ticket_info && (
              <div className="flex items-center px-4 py-2 bg-zinc-800/50 border border-zinc-700 font-mono text-sm text-zinc-300">
                <Ticket className="w-4 h-4 mr-2 text-primary" />
                {event.ticket_info}
              </div>
            )}
            <Button
              onClick={downloadICS}
              className="btn-ruido bg-[#111] border-2 border-zinc-700 text-white font-mono hover:border-primary"
              data-testid="download-ics"
            >
              <Download className="w-4 h-4 mr-2" />
              DESCARGAR .ICS
            </Button>
            <Button
              onClick={addToGoogleCalendar}
              className="btn-ruido bg-primary text-white font-mono border-2 border-primary"
              data-testid="add-google-calendar"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              AÑADIR A GOOGLE CALENDAR
            </Button>
          </div>

          {/* Description */}
          {event.description && (
            <div className="mb-8">
              <h2 className="text-xl font-black text-white mb-3">DESCRIPCIÓN</h2>
              <p className="text-zinc-400 whitespace-pre-wrap" data-testid="event-description">
                {event.description}
              </p>
            </div>
          )}

          {/* Music Genres */}
          {event.music_genres && event.music_genres.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-black text-white mb-3">GÉNERO MUSICAL</h2>
              <div className="flex flex-wrap gap-2">
                {event.music_genres.map((genre) => (
                  <span
                    key={genre}
                    className="bg-primary/20 text-primary font-mono text-sm px-3 py-1 uppercase"
                    data-testid={`event-genre-${genre}`}
                  >
                    {genre.replace(/-/g, " ")}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {event.tags && event.tags.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-black text-white mb-3">TIPO DE EVENTO</h2>
              <div className="flex flex-wrap gap-2">
                {event.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-zinc-800 text-zinc-300 font-mono text-sm px-3 py-1 uppercase"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bar Info */}
        {bar && (
          <div className="bg-[#111] border border-zinc-800 p-6 mb-8">
            <h2 className="text-xl font-black text-white mb-4">LUGAR</h2>
            <Link 
              to={`/bars/${bar.bar_id}`}
              className="group"
              data-testid="event-bar-link"
            >
              <h3 className="text-2xl font-bold text-white group-hover:text-primary transition-colors mb-2">
                {bar.name}
              </h3>
            </Link>
            {bar.address && (
              <div className="flex items-center gap-2 text-zinc-400 font-mono text-sm mb-4">
                <MapPin className="w-4 h-4 text-primary" />
                <span data-testid="event-bar-address">{bar.address}</span>
              </div>
            )}
            <Link 
              to={`/bars/${bar.bar_id}`}
              className="inline-flex items-center gap-2 font-mono text-sm text-primary hover:underline"
            >
              VER PERFIL DEL BAR
              <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        )}

        {/* Map */}
        {hasLocation && (
          <div className="mb-8">
            <h2 className="text-xl font-black text-white mb-4">UBICACIÓN</h2>
            <div className="h-64 border border-zinc-800 overflow-hidden" data-testid="event-map">
              <MapContainer
                center={[bar.latitude, bar.longitude]}
                zoom={15}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                <Marker position={[bar.latitude, bar.longitude]}>
                  <Popup>
                    <strong>{bar.name}</strong>
                    {bar.address && <br />}
                    {bar.address}
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
